// REACT
import React, { Fragment, useEffect, useRef, useState } from 'react';
import i18next from '../../i18n';

// YARN
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  tooltipClasses,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import { createTheme } from '@mui/material/styles';
import { frFR } from '@mui/material/locale';
import {
  GridEditInputCell,
  ColumnsPanelTrigger,
  Toolbar,
  ToolbarButton,
  useGridApiContext,
  GridEditSingleSelectCell,
  FilterPanelTrigger,
} from '@mui/x-data-grid-pro';
import { v4 as uuidv4 } from 'uuid';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { ThemeProvider } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AddIcon from '@mui/icons-material/Add';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';

// SERVICES
import {
  checkDecimalRegexTwoDigitsDatagridValue,
  checkTypeStringRegexDatagrid,
  getStringInsideParentheses,
  sortByLocalName,
} from '../tools_functions';
import { StyledTooltip } from './CustomGenericDataGridUtils';

// Theme
import {
  customButtonTheme,
  customToolbarButtonTheme,
} from '../../assets/styles/themes/generic_button_mui';
import { customWrappedDialogTheme } from 'assets/styles/themes/Dialog/generic_dialog_mui';
import { customCommonDataGridTheme } from 'assets/styles/themes/DataGrid/common_datagrid';

export const essentialColumns = {
  yieldProduct: false,
  coProduct: false,
  irrigationYieldGain: false,
  irrigationWaterNeeded: false,
  cultureSeason: false,
  cultureType: false,
  isStrawProducer: false,
  capCategory: false,
  isIrrigableOnly: false,
  isArable: false,
  priceN5: false,
  inputCostN5: false,
  tfiHerbicide: false,
  tfiWithoutHerbicide: false,
  nitrogenLimit: false,
  nitrogenBonus: false,
  inputCostWithoutNitrogen: false,
  inputCostWithoutNitrogenN5: false,
  grossMarginN5: false,
  aerialBiomass: false,
  rootBiomass: false,
  metaCode: false,
  metaCultureName: false,
  extraBiomass: false,
  firstSimulateEnabled: false,
};

export const allColumns = {
  priceN5: false,
  inputCostN5: false,
  tfiHerbicide: false,
  tfiWithoutHerbicide: false,
  inputCostWithoutNitrogen: false,
  inputCostWithoutNitrogenN5: false,
  grossMarginN5: false,
  metaCultureName: false,
};

export const CustomHeaderWithoutSort = ({ name }) => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      <div
        style={{
          height: '50px',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <StyledTooltip placement="top" title={name}>
          <span>{name}</span>
        </StyledTooltip>
      </div>
    </div>
  );
};

export const StyledTooltipError = styled(({ children, open, title, placement, className }) => (
  <Tooltip {...{ children, open, title, placement, className }} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    maxWidth: 120,
    fontSize: '12px',
  },
}));

export const StyledTooltipErrorYieldCoProduct = styled(
  ({ children, open, title, placement, className }) => (
    <Tooltip {...{ children, open, title, placement, className }} classes={{ popper: className }} />
  ),
)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    maxWidth: 150,
    fontSize: '12px',
    marginBottom: '50px !important',
  },
}));

export const StyledTooltipErrorInputCostModal = styled(
  ({ children, open, title, placement, className }) => (
    <Tooltip {...{ children, open, title, placement, className }} classes={{ popper: className }} />
  ),
)(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    minWidth: 190,
    fontSize: '12px',
    position: 'absolute',
    bottom: '15px',
    marginLeft: '8px !important',
  },
}));

export const StyledTooltipErrorNitrogen = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }}>
    {props.children}
  </Tooltip>
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    minWidth: 180,
    fontSize: '12px',
    position: 'absolute',
    bottom: '15px',
    right: '-90px',
  },
}));

export const CustomCultureColorCell = (color) => {
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {color && (
        <span className="cultureColorCircle" style={{ backgroundColor: `${color}` }}></span>
      )}
    </Box>
  );
};

export const CustomCulturePriceCell = ({ priceTabsValues, id, value, row }) => {
  const priceTabsValuesRef = priceTabsValues;
  const [currentValue, setCurrentValue] = useState(
    priceTabsValuesRef?.current?.find((row) => row.id === id)?.value ?? 0,
  );
  const handleChange = (_, newValue) => {
    setCurrentValue(+newValue);
    priceTabsValuesRef.current = priceTabsValuesRef.current.map((prevRow) =>
      prevRow.id === id ? { ...prevRow, value: newValue } : { ...prevRow },
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          margin: '3px 0 -5px 0',
        }}
      >
        <StyledTabs value={currentValue} onChange={handleChange} aria-label="basic tabs example">
          <StyledTab sx={{ width: '50%', minWidth: 'auto' }} label="N" {...a11yProps(0)} />
          <StyledTab sx={{ width: '50%', minWidth: 'auto' }} label="N+5" {...a11yProps(1)} />
        </StyledTabs>
      </Box>
      <CustomTabPanel sx={{ textAlign: 'center' }} value={currentValue} index={0}>
        <div>{value}</div>
      </CustomTabPanel>
      <CustomTabPanel sx={{ textAlign: 'center' }} value={currentValue} index={1}>
        <div>{row.priceN5}</div>
      </CustomTabPanel>
    </Box>
  );
};

export const CustomGrossMarginCell = ({ grossMarginTabsValuesRef, id, row }) => {
  const [value, setValue] = useState(
    grossMarginTabsValuesRef.current.find((row) => row?.id === id)?.value ?? 0,
  );
  const handleChange = (_, newValue) => {
    setValue(newValue);
    grossMarginTabsValuesRef.current = grossMarginTabsValuesRef.current.map((prevRow) =>
      prevRow.id === id ? { ...prevRow, value: newValue } : { ...prevRow },
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          margin: '3px 0 -5px 0',
        }}
      >
        <StyledTabs value={value} onChange={handleChange} aria-label="marge brute tabs">
          <StyledTab sx={{ width: '50%', minWidth: 'auto' }} label="N" {...a11yProps(0)} />
          <StyledTab sx={{ width: '50%', minWidth: 'auto' }} label="N+5" {...a11yProps(1)} />
        </StyledTabs>
      </Box>
      <CustomTabPanel sx={{ textAlign: 'center' }} value={value} index={0}>
        <div>{+row.grossMargin.toFixed(2)}</div>
      </CustomTabPanel>
      <CustomTabPanel sx={{ textAlign: 'center' }} value={value} index={1}>
        <div>{+row.grossMarginN5.toFixed(2)}</div>
      </CustomTabPanel>
    </Box>
  );
};

export const CustomEditCulturePriceCell = ({
  params,
  errorsFormEconomicValue,
  isOpen,
  priceTabsValuesRef,
}) => {
  const { row, id, field, value, hasFocus } = params;
  const [priceN5, setPriceN5] = useState(row.priceN5);
  const apiRef = useGridApiContext();
  const ref = useRef(null);
  const [editValue, setEditValue] = useState(
    priceTabsValuesRef?.current?.find((row) => row.id === params.id)?.value ?? 0,
  );

  let selectedField = field;

  let rowTabValue;
  if (priceTabsValuesRef.current.length > 0) {
    rowTabValue = priceTabsValuesRef?.current.find((row) => row.id === params.id);
  }

  const handleChange = (_, newValue) => {
    setEditValue(newValue);

    priceTabsValuesRef.current = priceTabsValuesRef.current.map((prevRow) =>
      prevRow.id === params.id ? { ...prevRow, value: newValue } : { ...prevRow },
    );
  };

  const handlePriceChange = (e) => {
    const {
      target: { value },
    } = e;
    setPriceN5(+value);
    apiRef.current.setEditCellValue({ id, field: 'priceN5', value });
  };

  if (rowTabValue?.value === 1) {
    selectedField = 'priceN5';
  }

  useEffect(() => {
    if (hasFocus && ref?.current) {
      const input = ref?.current?.querySelector('#priceN5');
      input?.focus();
    }
  }, [hasFocus, value]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', margin: '3px 0 -5px 0' }}>
        {errorsFormEconomicValue.formError &&
          errorsFormEconomicValue.hasOwnProperty(selectedField) &&
          errorsFormEconomicValue[selectedField].message.length > 0 && (
            <StyledTooltipError
              open={isOpen}
              title={errorsFormEconomicValue[selectedField].message}
              placement="top"
            >
              <span></span>
            </StyledTooltipError>
          )}
        <StyledTabs value={editValue} onChange={handleChange} aria-label="basic tabs example">
          <StyledTab sx={{ width: '50%', minWidth: 'auto' }} label="N" {...a11yProps(0)} />
          <StyledTab sx={{ width: '50%', minWidth: 'auto' }} label="N+5" {...a11yProps(1)} />
        </StyledTabs>
      </Box>
      <CustomTabPanel value={editValue} index={0}>
        <GridEditInputCell
          {...params}
          sx={{
            borderBottom:
              errorsFormEconomicValue.formError &&
              errorsFormEconomicValue.hasOwnProperty(selectedField) &&
              errorsFormEconomicValue[selectedField].message.length > 0
                ? '2px solid #d32f2f'
                : '1px solid #e69636',
          }}
        />
      </CustomTabPanel>
      <CustomTabPanel value={editValue} index={1}>
        <TextField
          sx={{
            padding: '0 16px',
            borderBottom:
              errorsFormEconomicValue.formError &&
              errorsFormEconomicValue.hasOwnProperty(selectedField) &&
              errorsFormEconomicValue[selectedField].message.length > 0
                ? '2px solid #d32f2f'
                : '1px solid #e69636',
          }}
          value={priceN5}
          onChange={(e) => handlePriceChange(e)}
          id="priceN5"
          type="number"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          variant="standard"
          size="small"
          ref={ref}
        />
      </CustomTabPanel>
    </Box>
  );
};

export const EditInputCell = (props, errorsFormDefault, isOpen) => {
  if (props.field === 'surface') {
    props.value = props.value.surface;
  }
  return errorsFormDefault.formError &&
    errorsFormDefault.hasOwnProperty(props.field) &&
    errorsFormDefault[props.field].message.length > 0 ? (
    <div>
      <StyledTooltipError
        open={isOpen}
        title={errorsFormDefault[props.field].message}
        placement="top"
      >
        <span>
          <GridEditInputCell {...props} sx={{ borderBottom: '2px solid #d32f2f' }} />
        </span>
      </StyledTooltipError>
    </div>
  ) : (
    <GridEditInputCell {...props} sx={{ borderBottom: '1px solid #e69636' }} />
  );
};

export const EditSelectCell = (props, errorsFormDefault, isOpen) => {
  const hasError =
    errorsFormDefault.formError &&
    errorsFormDefault.hasOwnProperty(props.field) &&
    errorsFormDefault[props.field].message.length > 0;

  return hasError ? (
    <StyledTooltipError
      open={isOpen}
      title={errorsFormDefault[props.field].message}
      placement="top"
    >
      <div style={{ width: '100%' }}>
        <GridEditSingleSelectCell {...props} sx={{ borderBottom: '2px solid #d32f2f' }} />
      </div>
    </StyledTooltipError>
  ) : (
    <GridEditSingleSelectCell {...props} sx={{ borderBottom: '1px solid #e69636' }} />
  );
};

export const CustomNameCell = (name) => {
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div>{name}</div>
    </Box>
  );
};

export const EditNameCell = ({ params, errorsFormDefault, isOpen }) => {
  const { row, id, field, value, hasFocus } = params;
  const ref = useRef(null);
  const apiRef = useGridApiContext();
  const [name, setName] = useState(
    row.parentName === row.name ? '' : getStringInsideParentheses(row.name),
  );

  useEffect(() => {
    if (hasFocus && ref.current) {
      const input = ref.current?.querySelector('#name');
      input?.focus();
    }
  }, [hasFocus, value]);

  const handleNameChange = (e) => {
    const {
      target: { value },
    } = e;
    setName(checkTypeStringRegexDatagrid(value));
    let newValue = !value.length ? row.parentName : `${row.parentName} (${value}))`;
    apiRef.current.setEditCellValue({ id, field: 'name', value: newValue });
  };
  const isError =
    errorsFormDefault.formError &&
    errorsFormDefault.hasOwnProperty(field) &&
    errorsFormDefault[field].message.length > 0;

  return (
    <Box sx={{ width: '100%' }}>
      <Box>
        {isError && (
          <StyledTooltipError
            open={isOpen}
            title={errorsFormDefault[field].message.replace('champ Nom', i18next.t('form.suffix'))}
            placement="top"
          >
            <span></span>
          </StyledTooltipError>
        )}
      </Box>
      <span style={{ cursor: 'default' }}>{row.parentName}</span>
      <TextField
        sx={{
          padding: '0 16px',
          borderBottom: isError ? '2px solid #d32f2f' : '1px solid #e69636',
        }}
        value={name}
        placeholder={i18next.t('placeholder.enterSuffix')}
        onChange={(e) => handleNameChange(e)}
        id="name"
        slotProps={{
          inputLabel: {
            shrink: true,
          },
        }}
        variant="standard"
        size="small"
        inputRef={ref}
      />
    </Box>
  );
};

export const CustomCultureInputCostCell = (
  params,
  openInputCostDialog,
  setOpenInputCostDialog,
  setInputCostValue,
  setParamsInputCost,
  isEditInputCost,
  inputCostTabsValuesRef,
) => {
  const [value, setValue] = useState(
    inputCostTabsValuesRef?.current?.find((row) => row.id === params.id)?.value ?? 0,
  );
  const setInputCostValues = () => {
    setInputCostValue((prev) => ({
      ...prev,
      inputCostWithoutNitrogen: params.row.inputCostWithoutNitrogen,
      inputCostWithoutNitrogenN5: params.row.inputCostWithoutNitrogenN5,
      necessaryNitrogenQuantity: params.row.necessaryNitrogenQuantity,
      inputCost: +params.value,
      inputCostN5: +params.row.inputCostN5,
      cultureId: +params.row.id,
    }));
  };
  const setInputCostParams = () => {
    setParamsInputCost((prev) => ({
      ...prev,
      inputCost: true,
    }));
  };
  const setInputCostN5Params = () => {
    setParamsInputCost((prev) => ({
      ...prev,
      inputCostN5: true,
    }));
  };
  const handleChange = (_, newValue) => {
    setValue(newValue);
    inputCostTabsValuesRef.current = inputCostTabsValuesRef.current.map((prevRow) =>
      prevRow.id === params.id ? { ...prevRow, value: newValue } : { ...prevRow },
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', margin: '3px 0 -5px 0' }}>
        <StyledTabs onChange={handleChange} value={value} aria-label="basic tabs example">
          <StyledTab sx={{ width: '50%', minWidth: 'auto' }} label="N" {...a11yProps(0)} />
          <StyledTab sx={{ width: '50%', minWidth: 'auto' }} label="N+5" {...a11yProps(1)} />
        </StyledTabs>
      </Box>
      <CustomTabPanel
        value={value}
        sx={{
          textAlign: 'center',
        }}
        onClick={() => {
          if (isEditInputCost) {
            setOpenInputCostDialog(!openInputCostDialog);
            setInputCostValues();
            setInputCostParams();
          }
        }}
        index={0}
      >
        <div>{params.value}</div>
      </CustomTabPanel>
      <CustomTabPanel
        value={value}
        sx={{
          textAlign: 'center',
        }}
        onClick={() => {
          if (isEditInputCost) {
            setOpenInputCostDialog(!openInputCostDialog);
            setInputCostValues();
            setInputCostN5Params();
          }
        }}
        index={1}
      >
        <div>{params.row.inputCostN5}</div>
      </CustomTabPanel>
    </Box>
  );
};

export const CustomCultureIFTCell = (
  params,
  isEditableIFT,
  openIFTDialog,
  setOpenIFTDialog,
  setIFTtValue,
) => {
  const setIFTValues = () => {
    setIFTtValue((prev) => ({
      ...prev,
      tfiHerbicide: params.row.tfiHerbicide,
      tfiWithoutHerbicide: params.row.tfiWithoutHerbicide,
      cultureId: params.row.id,
    }));
  };
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        onClick={() => {
          if (isEditableIFT) {
            setOpenIFTDialog(!openIFTDialog);
            setIFTValues();
          }
        }}
      >
        {params.row.tfi}
      </div>
    </Box>
  );
};

export const CustomMetaCultureCell = (
  params,
  isEditMetaculture,
  openMetacultureDialog,
  setOpenMetacultureDialog,
  setMetacultureValue,
) => {
  const setMetaCultureValues = () => {
    setMetacultureValue((prev) => ({
      ...prev,
      metaCultureName: params.row.metaCultureName,
      metaCode: params.row.metaCode,
      rootBiomass: +params.row.rootBiomass,
      aerialBiomass: +params.row.aerialBiomass,
      isArable: params.row.isArable,
      capCategory: params.row.capCategory,
      cultureId: +params.row.id,
    }));
  };
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        onClick={() => {
          if (isEditMetaculture) {
            setOpenMetacultureDialog(!openMetacultureDialog);
            setMetaCultureValues();
          }
        }}
      >
        <StyledTooltip placement="top" title={params.row.metaCode}>
          {params.row.metaCode}
        </StyledTooltip>
      </div>
    </Box>
  );
};

export const CustomNitrogenSupplyByCoverConventionalOrOrganic = ({
  errorsFormOffCropSettings,
  owner,
  nitrogenCoverClone,
  nitrogenSupplyByCoverOrganicClone,
  handleChangeNitrogenSupplyByCover,
  handleChangeNitrogenSupplyByCoverOrganic,
  isOpen,
  isOrganic,
}) => {
  return (
    <Box>
      <StyledTooltip placement="top" title={i18next.t('label.coverCropNitrogenTooltip')}>
        <InputLabel id="nitrogenSupplyByCoverLabel" htmlFor="nitrogenSupplyByCover">
          {i18next.t('form.coverCropNitrogenAmount')}
        </InputLabel>
      </StyledTooltip>
      {errorsFormOffCropSettings?.['nitrogenSupplyByCover'].message && (
        <div>
          <StyledTooltipErrorInputCostModal
            open={isOpen}
            title={errorsFormOffCropSettings?.['nitrogenSupplyByCover'].message}
            placement="top"
          >
            <span></span>
          </StyledTooltipErrorInputCostModal>
        </div>
      )}
      {isOrganic ? (
        <TextField
          id="nitrogenSupplyByCoverOrganic"
          disabled={owner ? true : false}
          value={nitrogenSupplyByCoverOrganicClone?.nitrogenSupplyByCoverOrganic}
          placeholder={i18next.t('placeholder.enterCoverCropNitrogenAmount')}
          onChange={(e) => handleChangeNitrogenSupplyByCoverOrganic(e)}
          className={
            errorsFormOffCropSettings?.['nitrogenSupplyByCover'].message ? 'MuiErrorForm' : ''
          }
        />
      ) : (
        <TextField
          id="nitrogenSupplyByCover"
          disabled={owner ? true : false}
          value={nitrogenCoverClone?.nitrogenSupplyByCover}
          placeholder={i18next.t('placeholder.enterCoverCropNitrogenAmount')}
          onChange={(e) => handleChangeNitrogenSupplyByCover(e)}
          className={
            errorsFormOffCropSettings?.['nitrogenSupplyByCover'].message ? 'MuiErrorForm' : ''
          }
        />
      )}
    </Box>
  );
};

const StyledTabs = styled(Tabs)({
  borderBottom: '1px solid #e8e8e8',
  '& .MuiTabs-indicator': {
    backgroundColor: '#e69636',
  },
});
const StyledTab = styled((props) => <Tab disableRipple {...props} />)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightRegular,
  color: '#277134',
  '&:hover': {
    color: '#e69636',
    opacity: 1,
  },
  '&.Mui-selected': {
    color: '#e69636',
    fontWeight: theme.typography.fontWeightMedium,
  },
  '&.Mui-focusVisible': {
    backgroundColor: '#d1eaff',
  },
}));

const CustomTabPanel = styled((props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ padding: '15px' }}>{children}</Box>}
    </div>
  );
})({});

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export const copyCurrentValueToFuture = (futureValue, currentValue) => {
  if (!futureValue && futureValue != '0') {
    return currentValue;
  }
  return futureValue;
};

export const customCoProductsCell = (value, setCoProductYieldFocus) => {
  const setYieldCoProductValues = (index) => (e) => {
    if (e.target?.innerText) {
      setCoProductYieldFocus({
        value: e.target?.innerText,
        index: `coProductYield-${index}`,
      });
    }
  };
  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '5px',
        whiteSpace: 'pre-wrap',
        textAlign: 'center',
      }}
    >
      {value?.length
        ? value?.map((val, index) => (
            <div
              style={{ userSelect: 'none' }}
              key={uuidv4()}
              onDoubleClick={setYieldCoProductValues(index)}
              id={`coProductYield-${index}`}
            >
              {val}
            </div>
          ))
        : '-'}
    </Box>
  );
};

export const CoProductYieldEditCell = ({
  id,
  value,
  field,
  hasFocus,
  row: { coProducts, coProduct },
  errorsFormCoProduct,
  errorCoProductsMessages,
  isOpen,
  coProductYieldFocus,
}) => {
  const apiRef = useGridApiContext();
  const ref = useRef(null);
  const [coProductsYield, setCoProductsYield] = useState(
    coProducts?.map((coProduct) => coProduct.yield),
  );
  const coProductsDisplayName = coProduct;

  useEffect(() => {
    if (!value) {
      setCoProductsYield(undefined);
    } else {
      setCoProductsYield(value);
    }
  }, [value]);

  const handleChange = (e, index) => {
    const {
      target: { value },
    } = e;
    const updatedCoProducts = coProductsYield?.map((prevCoProduct, prevCoProductIndex) =>
      index === prevCoProductIndex ? checkDecimalRegexTwoDigitsDatagridValue(value) : prevCoProduct,
    );
    setCoProductsYield(updatedCoProducts);
    apiRef.current.setEditCellValue({ id, field, value: updatedCoProducts });
  };
  useEffect(() => {
    if (hasFocus && coProductYieldFocus) {
      const input = ref.current?.querySelector(`[value="${coProductYieldFocus.value}"]`);
      const getId = input ? +input.id : null;
      if (getId === coProductYieldFocus.index) {
        input?.focus();
      }
    }
  }, [hasFocus, value, coProductYieldFocus]);

  return (
    <div ref={ref}>
      <ThemeProvider theme={customYieldEditTheme}>
        {coProductsYield?.length &&
          coProductsYield.map((coProductYield, index) => (
            <Fragment key={index}>
              <Box>
                {errorsFormCoProduct.formError && errorCoProductsMessages?.[index]?.length > 0 && (
                  <StyledTooltipErrorYieldCoProduct
                    open={isOpen}
                    title={errorCoProductsMessages[index]}
                    placement="top-start"
                  >
                    <span></span>
                  </StyledTooltipErrorYieldCoProduct>
                )}
              </Box>
              <TextField
                value={coProductYield}
                onChange={(e) => handleChange(e, index)}
                id={`coProductYield-${index}`}
                label={i18next.t('form.coProductYieldFor', {
                  name: coProductsDisplayName[index],
                })}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                variant="standard"
                size="small"
                className={
                  errorsFormCoProduct.formError && errorCoProductsMessages?.[index]?.length
                    ? 'MuiErrorForm'
                    : ''
                }
              />
            </Fragment>
          ))}
      </ThemeProvider>
    </div>
  );
};

export const CoProductEditCell = ({
  id,
  value,
  field,
  hasFocus,
  row: { yieldProduct, coProducts },
  coProductsList,
  setIsEditCoProduct,
}) => {
  const apiRef = useGridApiContext();
  const ref = useRef(null);
  const modalRef = useRef(null);
  const [coProductsRow, setCoProductsRow] = useState(
    coProducts?.map((coProduct) => coProduct.displayName),
  );
  const [open, setOpen] = useState(false);
  const [newYieldProduct, setNewYieldProduct] = useState(0);

  useEffect(() => {
    if (coProductsRow?.length && !coProductsRow.includes(i18next.t('common.none'))) {
      checkYield();
    }
  }, [coProductsRow]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setNewYieldProduct(0);
    setOpen(false);
  };

  const submitNewYieldValue = () => {
    apiRef.current.setEditCellValue({
      id,
      field: 'yieldProduct',
      value: yieldProduct ? [...yieldProduct, newYieldProduct] : [newYieldProduct],
    });
    handleClose();
  };
  const handleChange = (e, index) => {
    const {
      target: { value },
    } = e;
    const updatedCoProducts = coProductsRow?.map((prevCoProduct, prevCoProductIndex) =>
      index === prevCoProductIndex ? value : prevCoProduct,
    );
    setCoProductsRow(updatedCoProducts);
    apiRef.current.setEditCellValue({ id, field, value: updatedCoProducts });
  };

  const handleDelete = (index) => {
    setCoProductsRow((prevCoProducts) => {
      const filterCoProduct = prevCoProducts?.filter(
        (_, prevCoProductIndex) => index !== prevCoProductIndex,
      );
      const filterYieldProduct = yieldProduct?.filter((_, yieldIndex) => index !== yieldIndex);
      apiRef?.current?.setEditCellValue({ id, field, value: filterCoProduct });
      apiRef?.current?.setEditCellValue({ id, field: 'yieldProduct', value: filterYieldProduct });
      return filterCoProduct;
    });
  };
  const handleAdd = () => {
    coProductsRow?.length
      ? setCoProductsRow((prevCoProducts) => [...prevCoProducts, i18next.t('common.none')])
      : setCoProductsRow([i18next.t('common.none')]);
    coProductsRow?.length;
    setIsEditCoProduct(true);
  };
  const checkYield = () => {
    if (yieldProduct?.length !== coProductsRow?.length) {
      handleOpen();
    }
  };
  const handleYieldProduct = (e) => {
    setNewYieldProduct(checkDecimalRegexTwoDigitsDatagridValue(e.target.value));
  };

  useEffect(() => {
    if (hasFocus) {
      const input = ref?.current?.querySelector('#coProductSelect');
      if (open && input) {
        const inputModal = modalRef?.current?.querySelector('#coProductYield');
        inputModal?.focus();
      }
    }
  }, [hasFocus, value, ref, open, modalRef]);

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };

  return (
    <div>
      <ThemeProvider theme={customCoProductTheme}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {coProductsList && coProductsRow?.length ? (
            coProductsRow.map((coProduct, index) => (
              <FormControl
                fullWidth
                variant="filled"
                size="small"
                key={index}
                sx={{ flexDirection: 'row', alignItems: 'center' }}
              >
                <InputLabel
                  sx={{
                    '& .MuiInputLabel-root:focus': {
                      color: 'unset !important',
                    },
                  }}
                  id="coProductLabel"
                >
                  {i18next.t('form.coProductTypeNumber', { index: index + 1 })}
                </InputLabel>
                <Select
                  labelId="coProductLabel"
                  id="coProductSelect"
                  value={coProduct}
                  label={i18next.t('form.coProductType')}
                  onChange={(e) => handleChange(e, index)}
                  ref={ref}
                >
                  {sortByLocalName(coProductsList, 'displayName')
                    .filter((coProductListItem) => {
                      if (index !== 0) {
                        return !coProductsRow
                          .slice(0, index)
                          .includes(coProductListItem.displayName);
                      }
                      return coProductListItem;
                    })
                    .map((coProducts) => (
                      <MenuItem key={coProducts.id ?? 0} value={coProducts.displayName}>
                        {coProducts.displayName}
                      </MenuItem>
                    ))}
                </Select>
                {index + 1 === coProductsRow.length &&
                  coProductsRow[0] !== `${i18next.t('common.none')}]` && (
                    <AddCircleIcon sx={{ color: '#e69636' }} onClick={handleAdd} />
                  )}
                <DeleteIcon onClick={() => handleDelete(index)} sx={{ color: '#e69636' }} />
              </FormControl>
            ))
          ) : coProductsList ? (
            <ThemeProvider theme={customButtonTheme}>
              <Button onClick={handleAdd}>{i18next.t('common.add')}</Button>
            </ThemeProvider>
          ) : (
            <div style={{ color: 'red', fontSize: '10px' }}>
              {i18next.t('message.noCoProductRegistered')}
            </div>
          )}
        </div>

        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          keepMounted={true}
          ref={modalRef}
        >
          <Box sx={style}>
            <p>{i18next.t('message.enterCoProductYieldFor', { name: coProductsRow?.slice(-1) })}</p>
            <div>
              <TextField
                value={newYieldProduct}
                onChange={handleYieldProduct}
                id="coProductYield"
                label={i18next.t('form.coProductYield')}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                variant="standard"
                size="small"
              />
              <ThemeProvider theme={customButtonTheme}>
                <Button onClick={submitNewYieldValue}>{i18next.t('common.validate')}</Button>
              </ThemeProvider>
            </div>
          </Box>
        </Modal>
      </ThemeProvider>
    </div>
  );
};

export const CustomToolbarCultures = ({
  isLargeGrid,
  handleVisibility,
  toggleVisibilityFilter,
  selectedRows,
  handleCulturesShow,
  handleCulturesHide,
  badgeContent,
  from,
  addCultureModal,
  loadMetacultureData,
  setAddCultureModal,
  setOpenResetDialog,
  organicState,
  setOpenMaskCulturesModal,
}) => {
  return (
    <ThemeProvider theme={customToolbarButtonTheme}>
      <Toolbar>
        <Tooltip title={i18next.t('common.columns')}>
          <ColumnsPanelTrigger render={<ToolbarButton />}>
            <ViewColumnIcon fontSize="small" className="columnsPanelTrigger" />
            <div className="columnsPanelTriggerText">
              {i18next.t('common.columns').toUpperCase()}
            </div>
          </ColumnsPanelTrigger>
        </Tooltip>
        {from === 'fromExploitation' ? (
          <div>
            <Button onClick={() => setOpenMaskCulturesModal(true)}>
              {i18next.t('button.hideCultures')}
              <VisibilityIcon />
            </Button>
          </div>
        ) : toggleVisibilityFilter?.[organicState] ? (
          <div>
            <Button onClick={handleCulturesShow}>
              {i18next.t('button.showCultures')}
              <VisibilityOffIcon />
              <StyledBadge badgeContent={badgeContent} color="primary"></StyledBadge>
            </Button>
          </div>
        ) : (
          <div>
            <Button onClick={handleCulturesHide} disabled={!selectedRows?.ids?.size}>
              {i18next.t('button.hideCultures')}
              <VisibilityIcon />
            </Button>
          </div>
        )}
        <div>
          <Button onClick={handleVisibility}>
            {isLargeGrid ? i18next.t('button.shrinkTable') : i18next.t('button.expandTable')}
          </Button>
        </div>
        {from === 'fromCooperative' && (
          <div>
            <Button
              onClick={() => {
                (setAddCultureModal(!addCultureModal), loadMetacultureData());
              }}
            >
              {i18next.t('common.add')} <AddIcon />
            </Button>
          </div>
        )}
        {(from === 'fromSector' || from === 'fromExploitation') && (
          <div>
            <Button
              onClick={() => {
                setOpenResetDialog(true);
              }}
            >
              {i18next.t('button.resetGroundTypeEffects')}
            </Button>
          </div>
        )}
        <FilterPanelTrigger
          render={
            <Button startIcon={<FilterListIcon fontSize="small" />}>
              {i18next.t('common.filters').toUpperCase()}
            </Button>
          }
        />
      </Toolbar>
    </ThemeProvider>
  );
};

const mediaQuery = createTheme({
  breakpoints: {
    values: {
      xs: 640,
      sm: 1024,
      md: 1650,
      lg: 1920,
      xl: 2559,
    },
  },
});

export const customCultureDataGridTheme = createTheme(
  customCommonDataGridTheme,
  {
    components: {
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: '1px solid #277134 !important',
            '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
              border: 'none',
              outline: 'none',
            },
            '& .MuiDataGrid-cell.MuiDataGrid-cell--editing:focus-within': {
              border: 'none',
              outline: 'none',
              '& :focus-visible': {
                border: 'none',
                outline: 'none',
              },
            },
            '& .MuiDataGrid-virtualScroller': {
              maxHeight: '68vh',
              overflowY: 'auto !important',
              [`@media (max-width: ${mediaQuery.breakpoints.values.lg}px)`]: {
                overflowX: 'auto !important',
              },
            },
            '& .MuiDataGrid-columnHeaderTitleContainer .MuiCheckbox-root': {
              color: '#277134 !important',
            },
            '& .MuiDataGrid-cell--editing .MuiDataGrid-editInputCell .MuiInputBase-input': {
              textAlign: 'center !important',
            },
            '& .MuiBox-root .MuiFormControl-root .MuiInputBase-root:before, .MuiBox-root .MuiFormControl-root .MuiInputBase-root:after':
              {
                borderBottom: 'unset !important',
              },
            '& .MuiBox-root .MuiFormControl-root .MuiInput-input': {
              textAlign: 'center !important',
              padding: '0 16px',
              fontSize: '14px',
              height: 'unset',
            },
            '.MuiDataGrid-pinnedColumnHeaders--right': {
              paddingRight: '15px !important',
            },
            '& .MuiDataGrid-row--editing': {
              zIndex: 5,
              position: 'relative',
              boxShadow: '0px -1px 0px 1px #277134, 0px 1px 0px 1px #277134',
            },
            '& .MuiDataGrid-cell--Not-editable': {
              cursor: 'not-allowed',
              backgroundColor: '#d2d2d2 !important',
            },
            '& .MuiDataGrid-ParcelChild--row': {
              backgroundColor: '#d6d6d6 !important',
            },
            '& .MuiDataGrid--Not-editable': {
              cursor: 'default',
            },
            '& .MuiDataGrid--AllRows-editable': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-treeDataGroupingCellToggle .MuiIconButton-root': {
              fontWeight: 'bold',
              fontSize: '25px',
            },
            '& .select-capCategoryCell': {
              position: 'relative !important',
              '& .MuiSelect-select': {
                whiteSpace: 'pre-wrap !important',
              },
            },
            '& .MuiErrorForm .MuiSelect-select::before': {
              content: '""',
              position: 'absolute',
              top: '85%',
              right: 0,
              width: '100%',
              borderBottom: '2px solid #d32f2f !important',
              borderRadius: 'unset',
            },
            '& .MuiErrorForm-checkbox .MuiSvgIcon-root': {
              border: '2px solid #d32f2f !important',
            },
            '& .custom-clear-button': {
              paddingLeft: '0 !important',
              width: '22px',
              height: '22px',
              opacity: 1,
            },
            '& [data-testid="CalendarIcon"]': {
              width: '22px',
              height: '22px',
              paddingLeft: '0 !important',
            },
          },
          main: {
            fontFamily: 'Rubik-Medium',
            fontSize: '16px',
            color: '#707070',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#e69636 !important',
              color: 'white',
            },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#e69636 !important',
              color: 'white',
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            border: '1px solid #e69636',
            fontFamily: 'Rubik',
            fontSize: '14px',
            color: '#707070',
            ':hover': {
              color: '#e69636',
              fontFamily: 'Rubik-Medium',
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          colorPrimary: {
            color: '#e69636 !important',
            alignItems: 'unset',
            justifyContent: 'unset',
            '&.Mui-checked': {
              color: '#277134 !important',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: {
            borderColor: 'unset !important',
            borderWidth: '0px !important',
          },
        },
      },
    },
  },
  frFR,
);

export const datagridMappingTheme = createTheme(customCultureDataGridTheme, {
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-virtualScroller': {
            maxHeight: '100%',
            overflowY: 'auto !important',
            [`@media (max-width: ${mediaQuery.breakpoints.values.lg}px)`]: {
              maxHeight: '100%',
              overflowY: 'auto !important',
            },
          },
        },
      },
    },
  },
});

export const customCoProductTheme = createTheme({
  components: {
    MuiFormControl: {
      styleOverrides: {
        root: {
          '&.MuiFormControl-root': {
            borderBottom: '1px solid #e69636',
            margin: '1px',
          },
          '& .MuiOutlinedInput-root.Mui-focused fieldset': {
            borderColor: '#e69636 !important',
            top: '-2px !important',
          },
          '& .MuiOutlinedInput-root.Mui-focused legend': {
            display: 'none !important',
          },
          '& .MuiInputBase-root': {
            backgroundColor: 'rgba(0, 0, 0, 0) !important',
            maxWidth: 120,
          },
          '& .MuiSvgIcon-root': {
            paddingTop: '10px',
            cursor: 'pointer',
          },
          '& .MuiInputBase-root:before': {
            borderBottom: 'unset !important',
          },
          '& .MuiInputBase-root:after ': {
            borderColor: '#e69636 !important',
          },
          '& .MuiInputBase-root .MuiFilledInput-input:focus ': {
            backgroundColor: 'unset !important',
          },
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          '& .MuiBox-root': {
            border: '3px solid #e69636 !important',
            borderRadius: '20px',
          },
          '& .MuiBox-root .MuiFormControl-root': {
            border: 'none !important',
            marginRight: '40px',
            width: '40%',
          },
          '& .MuiBox-root .MuiFormControl-root .MuiInput-root input': {
            borderBottom: '1px solid #e69636 !important',
          },
          '& .MuiBox-root .MuiFormControl-root .MuiInput-root:after': {
            border: '1px solid #e69636 !important',
          },
          '& .MuiBox-root div': {
            display: 'flex',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#e69636 !important',
          top: '-6px',
          left: '-6px',
        },
      },
    },
  },
});

export const customYieldEditTheme = createTheme({
  components: {
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-root:before, .MuiBox-root .MuiFormControl-root .MuiInputBase-root:after':
            {
              borderBottom: 'unset !important',
            },
          '& .MuiInputBase-root input': {
            textAlign: 'center !important',
            padding: '5px',
            borderBottom: '1px solid #e69636 !important',
          },
          '& .MuiInputBase-root:after ': {
            borderColor: '#e69636 !important',
          },
          '&.MuiErrorForm .MuiInputBase-root input': {
            borderBottom: '2px solid #d32f2f !important',
          },
          '&.MuiFormControl-root.MuiErrorForm .MuiInputBase-root:after ': {
            borderColor: '#d32f2f !important',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#e69636 !important',
          userSelect: 'none',
        },
      },
    },
  },
});

export const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: -17,
    top: -23,
    position: 'absolute',
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
    backgroundColor: '#e69636',
  },
}));

export const handleRowUpdateValidation = (
  row,
  errorsFormDefault,
  errorsFormCoProduct,
  errorsFormEconomicValue,
) => {
  return new Promise((resolve, reject) => {
    if (
      errorsFormDefault.formError === false &&
      errorsFormCoProduct.formError === false &&
      errorsFormEconomicValue.formError === false
    ) {
      resolve(row);
    } else {
      reject();
    }
  });
};

export const renderResetDialog = (openResetDialog, setOpenResetDialog, resetAllCultures) => {
  return (
    <ThemeProvider theme={customWrappedDialogTheme}>
      <Dialog maxWidth="xs" open={openResetDialog}>
        <DialogTitle>{i18next.t('common.warning')}</DialogTitle>
        <DialogContent dividers>{i18next.t('dialog.resetDefaultValuesQuestion')}</DialogContent>
        <DialogActions>
          <Button onClick={resetAllCultures}>
            {i18next.t('common.yes')} <DoneIcon />
          </Button>
          <Button onClick={() => setOpenResetDialog(false)}>
            {i18next.t('common.no')} <ClearIcon />
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};
